import * as React from "react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Button,
  TablePagination,
} from "@material-ui/core";
import { ToastContainer } from "react-toastify";
import { Spinner } from "../../components";
import ApiService from "../../service/ApiService";
import { DatabaseEntities } from "../../model/enum/database-entities.enum";
import RowHorizontal from "../../assets/admin/row-horizontal.svg";
import RowGrid from "../../assets/admin/row-grid.svg";
import ProjectModal from "./ProjectModal";
import ProjectDetailModal from "./ProjectDetailModal";
import Project from "../../model/project";
import Pathnames from "../../model/enum/Pathnames";
import PageResultResponse from "../../model/page-result-response";
import useStyles from "./styles";
import styles from "../styles/cardStyles";
import Misc from "../../utils/Misc";
import CachedIcon from "@mui/icons-material/Cached";

const Projects = () => {
  const classes = useStyles();
  const cardStyles = styles();

  const [projects, setProjects] = useState<Project[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [isRow, setIsRow] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  // Update the URL with the current page and rowsPerPage
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    url.searchParams.set("rowsPerPage", rowsPerPage.toString());
    window.history.pushState({}, Pathnames.PROJECTS_LIST, url);
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      searchParams.set("page", page.toString());
      searchParams.set("rowsPerPage", rowsPerPage.toString());

      await ApiService.get<PageResultResponse>(DatabaseEntities.PROJECTS)
        .then((data: PageResultResponse) => {
          if (Misc.isArrayEmpty(data.items)) return;
          const { items, count } = data;
          setProjects(items);
          setTotalPages(count);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    if (loading && Misc.isArrayEmpty(projects)) fetchData();
  }, [projects]);

  const resetList = (): void => {
    setPage(1);
    setRowsPerPage(10);
    setProjects([]);
    setLoading(true);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown> | null,
    pg: number,
  ) => {
    setPage(pg + 1);
    scrollToTop();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let row = parseInt(event.target.value);
    setRowsPerPage(row);
    scrollToTop();
  };

  const scrollToTop = () => {
    setProjects([]);
    setLoading(true);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const changeRowDisplay = (orientation: boolean) => {
    // setRowsPerPage(rowsPerPage === 10 ? 5 : 10);
    // setPage(0);
    // setItems(projects.slice(0, rowsPerPage));
    setIsRow(orientation);
  };

  const addIdToQueryPath = (id?: number) => {
    if (!id) return;

    const url = new URL(window.location.href);
    url.searchParams.set("projectId", id.toString());
    window.history.pushState({}, Pathnames.PROJECTS_LIST, url);
  };

  const removeIdFromQueryPath = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("projectId");
    window.history.replaceState({}, "", url.pathname + url.search);
  };

  return (
    <>
      <Card raised className={cardStyles.card}>
        <div className={cardStyles.headerContainer}>
          <Typography className={cardStyles.title}>Projects</Typography>
          <div className={cardStyles.headerDiv}>
            <Button onClick={resetList}>
              <CachedIcon />
            </Button>
            {window.innerWidth > 640 && (
              <>
                <Button onClick={() => changeRowDisplay(true)}>
                  <img src={RowGrid} alt="row grid" />
                </Button>
                <Button onClick={() => changeRowDisplay(false)}>
                  <img src={RowHorizontal} alt="col grid" />
                </Button>
              </>
            )}
          </div>
        </div>
        <CardContent
          className={isRow ? classes.containerRow : classes.containerCol}
        >
          <ProjectModal resetList={resetList} />
          {loading && <Spinner />}
          <>
            {projects.map((row: Project) => (
              <React.Fragment key={row.id}>
                <ProjectDetailModal
                  project={row}
                  resetList={resetList}
                  isRow={isRow}
                  onOpen={() => {
                    addIdToQueryPath(row?.id);
                  }}
                  onClose={() => {
                    removeIdFromQueryPath();
                  }}
                />
              </React.Fragment>
            ))}
          </>
        </CardContent>
        <TablePagination
          component="div"
          count={totalPages}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
      <ToastContainer />
    </>
  );
};

export default Projects;
